import React, { useState } from "react";
import { Alert, Box, IconButton, Modal, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import type { RemoveOrderLineModalProps } from "../interfaces/modals";
import { useRemoveOrderItem } from "../../../hooks/api/useSalesOrder";
import { isOrderMutationRejectedError } from "../../../hooks/api/orderMutationErrors";
import { messageForOrderMutationCode } from "../utils/orderErrorMessages";
import { orderItemLineLabel } from "../utils/orderUtils";

const RemoveOrderLineModal: React.FC<RemoveOrderLineModalProps> = ({
  open,
  onClose,
  orderId,
  line,
  onRemoved
}) => {
  const { t } = useKivunovaTranslation();
  const removeOrderItem = useRemoveOrderItem({ suppressErrorBanner: true });
  const [submitError, setSubmitError] = useState<string | null>(null);

  const busy = removeOrderItem.isPending;

  const handleClose = () => {
    if (busy) return;
    setSubmitError(null);
    onClose();
  };

  const handleConfirm = async () => {
    if (!line) return;
    setSubmitError(null);
    try {
      const order = await removeOrderItem.mutateAsync({
        orderId,
        productItemId: line.itemId
      });
      if (order) {
        await onRemoved();
        handleClose();
      } else {
        setSubmitError(
          t(
            "remove-line-failed",
            "Could not remove this line. Please try again."
          )
        );
      }
    } catch (e) {
      if (isOrderMutationRejectedError(e)) {
        setSubmitError(messageForOrderMutationCode(e.apiCode, t));
      } else {
        setSubmitError(
          t(
            "remove-line-unexpected-error",
            "Something went wrong. Please try again."
          )
        );
      }
    }
  };

  const label = line ? orderItemLineLabel(line) : t("order-line", "Order line");

  return (
    <Modal open={open} onClose={busy ? undefined : handleClose}>
      <Box className="modal-styles">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {t("remove-order-line", "Remove order line")}
          </Typography>
          <IconButton onClick={handleClose} size="small" disabled={busy}>
            <CloseIcon />
          </IconButton>
        </Box>

        {submitError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setSubmitError(null)}
          >
            {submitError}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t(
            "remove-order-line-confirm",
            "Remove this item from the order? Reservations and totals will be updated."
          )}
        </Typography>
        <Typography variant="body1" fontWeight="medium" sx={{ mb: 3 }}>
          {label}
          {line != null && (
            <>
              {" "}
              · {t("qty", "Qty")}: {line.quantity}
            </>
          )}
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            flexWrap: "wrap"
          }}
        >
          <LoadingButton
            variant="outlined"
            onClick={handleClose}
            disabled={busy}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2
            }}
          >
            {t("cancel", "Cancel")}
          </LoadingButton>
          <LoadingButton
            variant="contained"
            disableElevation
            onClick={() => void handleConfirm()}
            loading={busy}
            disabled={!line}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              backgroundColor: "error.main",
              color: "error.contrastText",
              "&:hover": { backgroundColor: "error.dark" }
            }}
          >
            {t("remove", "Remove")}
          </LoadingButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default RemoveOrderLineModal;
